import React from 'react';

import { type Shown, delta, formatExact, formatValue, memberLabel } from '../format';
import type { Palette } from '../palette';
import { overrideColor, seriesOverrides } from '../seriesStyle';
import { linkFor, toneFor } from '../tableExtras';
import type { Block, Envelope, EnvelopeColumn } from '../types';

type BreakdownProps = {
  block?: Block;
  envelope: Envelope;
  selected: Array<string>;
  onPick?: (member: string) => void;
  shown: Shown;
  colors?: Map<string, string>;
  palette?: Palette;
};

function shareText(share: number): string {
  if (share > 0 && share < 0.005) return '<1%';
  return `${Math.round(share * 100)}%`;
}

function ToneLabel({ text, tone }: { text: string; tone?: string }) {
  return tone ? <span className={`pill ${tone}`}>{text}</span> : <>{text}</>;
}

export default function Breakdown({ block, envelope, selected, onPick, shown, colors, palette }: BreakdownProps) {
  const overrides = seriesOverrides(block);
  const measure = envelope.columns.find((column) => column.role === 'measure') as EnvelopeColumn;
  const dim = (envelope.columns.find((column) => column.role === 'dim') ?? { name: '', role: 'dim', label: measure.label }) as EnvelopeColumn;
  const comparison = envelope.period.comparisons[0];
  const before = new Map(
    (comparison ? (envelope.compare[comparison.id] ?? []) : []).map((row) => [
      dim.name ? String(row[dim.name]) : measure.label,
      row[measure.name] as number | null,
    ]),
  );
  const total = envelope.totals?.current?.[measure.name] as number | null | undefined;
  const sampleOf = (row: (typeof envelope.rows)[number]) => row[`n:${measure.name}`] as number | null | undefined;
  const thin = (row: (typeof envelope.rows)[number]) => {
    const sample = sampleOf(row);
    return measure.minN !== undefined && sample !== null && sample !== undefined && sample < measure.minN;
  };
  const ranked = envelope.rows.filter((row) => !thin(row) && !overrides[dim.name ? String(row[dim.name]) : measure.name]?.hidden);
  const low = envelope.rows.filter(thin);
  const values = ranked.map((row) => (row[measure.name] as number | null) ?? 0);
  const largest = Math.max(...values.map(Math.abs), 0) || 1;
  const below = Math.max(0, ...values.map((value) => -value));
  const above = Math.max(0, ...values);
  const zero = below && above ? below / (below + above) : 0;
  const span = zero ? below + above : largest;
  const additive = measure.additive !== false && measure.format !== 'pct';
  const fill = typeof block.h === 'number' ? { '--bar': `${Math.round(Math.min(48, Math.max(6, (block.h / Math.max(ranked.length + low.length, 1)) * 0.45)))}px` } : undefined;
  return (
    <div className={`rank${additive ? '' : ' unshared'}${comparison ? ' compared' : ''}${fill ? ' fill' : ''}`} role="list" style={fill as React.CSSProperties | undefined}>
      {ranked.map((row) => {
        const member = dim.name ? String(row[dim.name]) : measure.label;
        const value = row[measure.name] as number | null;
        const change = delta(value, before.get(member), measure, shown.delta, shown.numbers);
        const pickable = Boolean(onPick) && !row.other;
        const key = row.other ? `other:${member}` : member;
        const state = selected.includes(member) ? 'on' : selected.length ? 'dim' : '';
        const content = (
          <>
            <span className="k" title={member}>
              {colors?.get(member) ? <i className="sw" style={{ background: colors.get(member) }} /> : null}
              {pickable || !linkFor(block, dim.name, row) ? (
                <ToneLabel text={overrides[member]?.label ?? memberLabel(member)} tone={toneFor(block, dim.name, member)} />
              ) : (
                <a className="lk" href={linkFor(block, dim.name, row)} rel="noopener noreferrer" target="_blank">
                  <ToneLabel text={overrides[member]?.label ?? memberLabel(member)} tone={toneFor(block, dim.name, member)} />
                </a>
              )}
            </span>
            <span className={zero ? 'bar diverging' : 'bar'} style={zero ? ({ '--zero': `${zero * 100}%` } as React.CSSProperties) : undefined}>
              <i
                className={zero && (value ?? 0) < 0 ? 'below' : undefined}
                style={{
                  ...(palette && overrides[member]?.color !== undefined ? { background: overrideColor(overrides[member], palette, '') } : {}),
                  width: `${(Math.abs(value ?? 0) / span) * 100}%`,
                  ...(zero ? { marginLeft: `${(zero - ((value ?? 0) < 0 ? Math.abs(value ?? 0) / span : 0)) * 100}%` } : {}),
                }}
              />
            </span>
            <span
              className="val"
              data-lens-ref={`${envelope.query}.${measure.name}[${member}]`}
              title={formatExact(value, measure.format)}
            >
              {formatValue(value, measure.format, { numbers: shown.numbers, decimals: measure.decimals })}
            </span>
            {additive ? <span className="share">{total ? shareText((value ?? 0) / total) : ''}</span> : null}
            <span className={`dl ${change?.tone ?? ''}`} data-lens-ref={`${envelope.query}.${measure.name}[${member}]@${comparison?.id ?? ''}`} title={comparison ? `vs ${comparison.label}` : undefined}><span>{change ? `${change.arrow} ${change.text}` : ''}</span>{shown.baseline && comparison ? <span> · {formatValue(before.get(member), measure.format, { numbers: shown.numbers, decimals: measure.decimals })}</span> : null}</span>
          </>
        );
        const className = ['r', pickable ? 'pick' : '', state].filter(Boolean).join(' ');
        return pickable ? (
          <button
            aria-pressed={state === 'on'}
            className={className}
            data-lens-part={`member:${member}`}
            key={key}
            onClick={() => onPick!(member)}
            role="listitem"
            type="button"
          >
            {content}
          </button>
        ) : (
          <div className={className} data-lens-part={`member:${member}`} key={key} role="listitem">
            {content}
          </div>
        );
      })}
      {low.length ? (
        <p className="low-sample">
          Low sample, n under {measure.minN}:{' '}
          {low
            .map((row) => `${memberLabel(String(row[dim.name]))} ${formatValue(row[measure.name] as number | null, measure.format, { numbers: shown.numbers, decimals: measure.decimals })} n=${sampleOf(row) ?? 0}`)
            .join(' · ')}
        </p>
      ) : null}
    </div>
  );
}
